import { TProfileDetailsData } from '../models/b2c/account';
import { TPaymentFormValues } from '../models/common/payments';
import {
  TB2BAddressRequestPhone,
  IB2BAddress,
  TB2BRequestAddressData,
} from '../models/b2b/addresses';
import {
  ADDRESS_TYPES,
  COUNTRIES,
  IAddressesCommonData,
  PHONE_TYPES,
  TAddressFormValues,
  TCommonPartialAddress,
  YES_NO_FLAGS,
  IPaginationInfo,
  IMappedAddress,
} from '../models/common/addresses';
import {
  IB2CAddressWithMetaData,
  TB2CRequestAddressData,
} from '../models/b2c/addresses';
import { removeNonDigitsFromString } from './general';

export const mapAddressListToCommonModel = (
  addresses: Partial<IB2BAddress>[] | Partial<IB2CAddressWithMetaData>[],
  paginationInfo?: Partial<IPaginationInfo>,
): IAddressesCommonData => {
  const mappedAddresses = addresses.map((address): IMappedAddress => {
    return {
      isDefault: address.isDefault || false,
      companyName: 'companyName' in address ? address.companyName : undefined,
      firstName: address.name?.firstName || '',
      lastName: address.name?.lastName || '',
      nickName:
        address.name && 'nickName' in address.name
          ? address.name.nickName
          : undefined,
      addedBy: 'addedBy' in address ? address.addedBy : undefined,
      primaryPhone:
        'phones' in address
          ? address.phones?.[0]?.number || ''
          : ('primaryPhone' in address && address.primaryPhone?.number) || '',
      alternatePhone:
        'phones' in address
          ? address.phones?.[1]?.number
          : ('alternatePhone' in address && address.alternatePhone?.number) ||
            '',
      addressLine1: address.postalDetails?.addressLine1 || '',
      addressLine2: address.postalDetails?.addressLine2,
      state: address.postalDetails?.state || '',
      zipCode: address.postalDetails?.zipCode || '',
      city: address.postalDetails?.city || '',
      userId: 'userId' in address ? address.userId : undefined,
      lastModifiedDate: address.lastModifiedDate,
      addrIdentifier: address.addrIdentifier as number,
      instantCheckoutDefault:
        'instantCheckoutDefault' in address
          ? address.instantCheckoutDefault
          : undefined,
    };
  });

  return {
    addresses: mappedAddresses,
    paginationInfo: {
      ...(paginationInfo || {}),
      addressesOnCurrentPage: addresses.length,
    },
    response: addresses,
  };
};

export const mapProfileDataToB2CAddAdressRequestModel = (
  profileData: TProfileDetailsData,
  paymentFormValues: TPaymentFormValues,
  isNoAddresses: boolean,
) => {
  const firstName =
    profileData.account.profile.name?.firstName ||
    paymentFormValues.cardHolderName.split(' ')[0];
  const lastName =
    profileData.account.profile.name?.lastName ||
    paymentFormValues.cardHolderName.split(' ')[1] ||
    '';
  const primaryPhone = removeNonDigitsFromString(
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    paymentFormValues.primaryPhone!.number,
  );

  const addressValues: TAddressFormValues = {
    firstName,
    lastName,
    primaryPhone,
    zipCode: paymentFormValues.zipCode,
    isDefault: false,
    addressLine1: paymentFormValues.addressLine1,
    addressLine2: paymentFormValues.addressLine2,
    cityState: paymentFormValues.cityState,
    companyName: '',
    alternatePhone: '',
    nickName: '',
  };

  return mapAddAddressToRequestModel(false, addressValues, isNoAddresses);
};

export const mapAddAddressToRequestModel = (
  isB2B: boolean,
  addressValues: TAddressFormValues,
  isNoAddresses: boolean,
): TB2BRequestAddressData | TB2CRequestAddressData => {
  const {
    companyName = '',
    isDefault,
    nickName,
    lastName,
    firstName,
    addressLine1,
    zipCode,
  } = addressValues;

  const cityState = addressValues.cityState.split(',');
  const city = cityState[0]?.trim();
  const state = cityState[1]?.trim();
  const addressLine2 = addressValues.addressLine2 || undefined;
  const primaryPhone = removeNonDigitsFromString(addressValues.primaryPhone);
  const alternatePhone = addressValues.alternatePhone
    ? removeNonDigitsFromString(addressValues.alternatePhone)
    : undefined;

  if (isB2B) {
    const phones: Array<TB2BAddressRequestPhone> = [
      {
        number: primaryPhone,
        primaryFlag: YES_NO_FLAGS.YES,
        type: PHONE_TYPES.UNKNOWN,
      },
    ];
    if (alternatePhone) {
      phones.push({
        number: alternatePhone,
        type: PHONE_TYPES.UNKNOWN,
        primaryFlag: YES_NO_FLAGS.NO,
      });
    }
    return {
      addressType: ADDRESS_TYPES.SHIPPING,
      companyName,
      isDefault,
      name: {
        firstName,
        lastName,
        nickName,
      },
      phones,
      postalDetails: {
        addressLine1,
        addressLine2,
        city,
        country: COUNTRIES.US,
        state,
        zipCode,
      },
    };
  }

  return {
    isDefault: !!isNoAddresses,
    name: {
      firstName,
      lastName,
    },
    postalDetails: {
      city,
      addressLine1,
      addressLine2,
      state,
      country: COUNTRIES.US,
      zipCode,
    },
    primaryPhone: {
      number: primaryPhone,
    },
    alternatePhone: alternatePhone
      ? {
          number: alternatePhone,
        }
      : undefined,
  };
};

export const mapEditAddressToRequestModel = (
  isB2B: boolean,
  addressValues: TAddressFormValues,
  fullAddressData: TCommonPartialAddress,
): TB2BRequestAddressData | TB2CRequestAddressData => {
  const alternatePhone = addressValues.alternatePhone
    ? removeNonDigitsFromString(addressValues.alternatePhone)
    : undefined;

  if (isB2B) {
    const b2bRequestData = mapAddAddressToRequestModel(
      isB2B,
      addressValues,
      false,
    ) as TB2BRequestAddressData;
    const phones = b2bRequestData.phones.map((phone, ind) => ({
      ...phone,
      id: fullAddressData.phones?.[ind]?.id,
    }));
    if (!alternatePhone && fullAddressData.phones?.[1]) {
      phones.push({
        id: fullAddressData.phones?.[1]?.id,
      });
    }
    b2bRequestData.phones = [...phones];
    b2bRequestData.level = fullAddressData.level;
    b2bRequestData.lastModifiedDate = fullAddressData.lastModifiedDate;
    b2bRequestData.addrIdentifier = fullAddressData.addrIdentifier;
    if (fullAddressData.userId) {
      b2bRequestData.userId = fullAddressData.userId;
    }
    return b2bRequestData;
  }

  const b2cRequestData = mapAddAddressToRequestModel(
    isB2B,
    addressValues,
    false,
  ) as TB2CRequestAddressData;

  b2cRequestData.isDefault = addressValues.isDefault;
  b2cRequestData.primaryPhone.contactMethodEnumeration =
    fullAddressData.primaryPhone?.contactMethodEnumeration;
  b2cRequestData.primaryPhone.id = fullAddressData.primaryPhone?.id;

  if (fullAddressData.alternatePhone) {
    b2cRequestData.alternatePhone = {
      number: alternatePhone || '',
      id: fullAddressData.alternatePhone.id,
    };
  }

  b2cRequestData.fraudChecked = fullAddressData.fraudChecked;
  b2cRequestData.instantCheckoutDefault =
    fullAddressData.instantCheckoutDefault;
  b2cRequestData.addrIdentifier = fullAddressData.addrIdentifier;
  b2cRequestData.lastModifiedDate = fullAddressData.lastModifiedDate;

  return b2cRequestData;
};

export const mapAddressFormToMappedModal = (address: TAddressFormValues) => {
  const [city, state] = address.cityState.split(', ');

  return {
    isDefault: address.isDefault || false,
    companyName: address.companyName || '',
    primaryPhone: address.primaryPhone || '',
    alternatePhone: address.alternatePhone || '',
    firstName: address.firstName || '',
    lastName: address.lastName || '',
    nickName: address.nickName || '',
    zipCode: address.zipCode || '',
    addressLine1: address.addressLine1 || '',
    addressLine2: address.addressLine2 || '',
    city,
    state,
  } as IMappedAddress;
};

export const mapB2CPaymentAddressToFormModel = (
  address: IB2CAddressWithMetaData,
  paymentValues: TPaymentFormValues,
): TAddressFormValues => ({
  isDefault: !!address?.isDefault,
  addressLine1: paymentValues.addressLine1,
  addressLine2: paymentValues.addressLine2,
  companyName: '',
  firstName: address.name.firstName,
  lastName: address.name.lastName,
  nickName: '',
  primaryPhone: paymentValues.primaryPhone?.number || '',
  alternatePhone: address.alternatePhone?.number,
  zipCode: paymentValues.zipCode,
  cityState: paymentValues.cityState,
});

export const mapB2CPaymentAddressToRequestModel = (
  address: IB2CAddressWithMetaData,
  paymentValues: TPaymentFormValues,
) => {
  const addressValues = mapB2CPaymentAddressToFormModel(address, paymentValues);
  return mapEditAddressToRequestModel(false, addressValues, address);
};
